import { useCallback, useEffect, useRef, useState } from "react";

import { getLocale } from "@recobra/i18n/runtime";

import { getSpeechRecognizerConstructor, type SpeechRecognizer } from "./WebSpeechRecognition";

type DictationState = "unsupported" | "idle" | "preparing" | "listening";

export type DictationErrorKey =
  | "dictation_error_not_allowed"
  | "dictation_error_no_speech"
  | "dictation_error_generic";

export type DictationTranscript = { final: string; interim: string };

const recognitionLanguage = () => (getLocale() === "en" ? "en-US" : getLocale());

const errorKeyFor = (error: string): DictationErrorKey => {
  if (error === "not-allowed" || error === "service-not-allowed") {
    return "dictation_error_not_allowed";
  }
  if (error === "no-speech") return "dictation_error_no_speech";

  return "dictation_error_generic";
};

/**
 * Live speech-to-text via the Web Speech API, preferring on-device recognition
 * where the browser has a local model and falling back to its cloud service.
 *
 * Reports `"unsupported"` where the API is absent (notably Firefox); callers are
 * expected to render nothing in that case.
 */
export const useDictation = ({
  onResult,
  onError,
  onEnd,
}: {
  onResult: (transcript: DictationTranscript) => void;
  onError: (messageKey: DictationErrorKey) => void;
  onEnd: () => void;
}) => {
  const [state, setState] = useState<DictationState>(() =>
    getSpeechRecognizerConstructor() ? "idle" : "unsupported",
  );
  const recognizerRef = useRef<SpeechRecognizer | null>(null);
  const finalTranscriptRef = useRef("");
  const callbacksRef = useRef({ onResult, onError, onEnd });
  const mountedRef = useRef(false);
  const startRequestRef = useRef(0);
  const preparingRef = useRef(false);

  useEffect(() => {
    callbacksRef.current = { onResult, onError, onEnd };
  }, [onResult, onError, onEnd]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      startRequestRef.current += 1;
      preparingRef.current = false;
      recognizerRef.current?.abort();
      recognizerRef.current = null;
    };
  }, []);

  const stop = useCallback(() => {
    startRequestRef.current += 1;
    preparingRef.current = false;
    const recognizer = recognizerRef.current;
    if (recognizer) {
      recognizer.stop();
    } else if (mountedRef.current) {
      setState("idle");
    }
  }, []);

  const start = useCallback(async () => {
    const constructor = getSpeechRecognizerConstructor();
    if (!constructor || recognizerRef.current || preparingRef.current) return;

    const requestId = ++startRequestRef.current;
    preparingRef.current = true;
    const lang = recognitionLanguage();
    let processLocally = false;

    if (constructor.available) {
      setState("preparing");
      const availability = await constructor
        .available({ langs: [lang], processLocally: true, quality: "dictation" })
        .catch(() => "unavailable" as const);

      if (availability === "available") {
        processLocally = true;
      } else if (availability === "downloadable" || availability === "downloading") {
        void constructor.install?.({ langs: [lang], processLocally: true }).catch(() => undefined);
      }
    }

    if (!mountedRef.current || requestId !== startRequestRef.current) return;
    preparingRef.current = false;

    const recognizer = new constructor();
    recognizer.lang = lang;
    recognizer.continuous = true;
    recognizer.interimResults = true;
    if ("processLocally" in recognizer) recognizer.processLocally = processLocally;
    if ("unspokenPunctuation" in recognizer) recognizer.unspokenPunctuation = true;

    recognizer.onresult = (event) => {
      let interim = "";
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        const result = event.results[index];
        const transcript = result?.[0]?.transcript ?? "";
        if (result?.isFinal) finalTranscriptRef.current += transcript;
        else interim += transcript;
      }
      callbacksRef.current.onResult({ final: finalTranscriptRef.current, interim });
    };

    recognizer.onerror = (event) => {
      if (event.error === "aborted") return;
      callbacksRef.current.onError(errorKeyFor(event.error));
    };

    recognizer.onend = () => {
      if (recognizerRef.current === recognizer) recognizerRef.current = null;
      if (!mountedRef.current) return;
      setState("idle");
      callbacksRef.current.onEnd();
    };

    finalTranscriptRef.current = "";
    recognizerRef.current = recognizer;

    try {
      recognizer.start();
      setState("listening");
    } catch {
      if (recognizerRef.current === recognizer) recognizerRef.current = null;
      setState("idle");
      callbacksRef.current.onError("dictation_error_generic");
    }
  }, []);

  return { state, start, stop };
};
